import { useState } from 'react'
import { request, gql } from 'graphql-request'
import { useSlowRefreshEffect } from './useRefreshEffect'

export enum SubgraphStatus {
  OK,
  WARNING,
  NOT_OK,
  UNKNOWN,
  DOWN,
}

export type SubgraphHealthState = {
  status: SubgraphStatus
  currentBlock: number
  chainHeadBlock: number
  latestBlock: number
  blockDifference: number
}

const NOT_OK_BLOCK_DIFFERENCE = 200 // ~15 minutes delay
const WARNING_BLOCK_DIFFERENCE = 50 // ~2.5 minute delay

/**
 * Health of a subgraph, measured against the head of the chain the app is
 * currently connected to.
 *
 * This queries the standard `_meta` field on the subgraph's own query endpoint
 * rather than `indexingStatusForCurrentVersion`. The latter only exists on
 * graph-node's index-node API (port 8030), which our indexer does not expose
 * publicly, so it failed for every subgraph and reported them all as DOWN.
 *
 * @param subgraphUrl full GraphQL URL of the subgraph, e.g.
 *                    `https://subgraphs.9inch.io/subgraphs/name/pulsechain/v3`
 */
const useSubgraphHealth = (subgraphUrl?: string) => {
  const [sgHealth, setSgHealth] = useState<SubgraphHealthState>({
    status: SubgraphStatus.UNKNOWN,
    currentBlock: 0,
    chainHeadBlock: 0,
    latestBlock: 0,
    blockDifference: 0,
  })

  useSlowRefreshEffect(
    (currentBlock) => {
      const getSubgraphHealth = async () => {
        try {
          const { _meta: meta } = await request<{
            _meta: { block: { number: number }; hasIndexingErrors: boolean } | null
          }>(
            subgraphUrl,
            gql`
              query getSubgraphHealth {
                _meta {
                  block {
                    number
                  }
                  hasIndexingErrors
                }
              }
            `,
          )

          if (!meta) {
            throw new Error(`Subgraph ${subgraphUrl} returned no _meta`)
          }

          const latestBlock = Number(meta.block.number)
          const blockDifference = currentBlock - latestBlock

          if (meta.hasIndexingErrors || blockDifference > NOT_OK_BLOCK_DIFFERENCE) {
            setSgHealth({
              status: SubgraphStatus.NOT_OK,
              currentBlock,
              chainHeadBlock: currentBlock,
              latestBlock,
              blockDifference,
            })
          } else if (blockDifference > WARNING_BLOCK_DIFFERENCE) {
            setSgHealth({
              status: SubgraphStatus.WARNING,
              currentBlock,
              chainHeadBlock: currentBlock,
              latestBlock,
              blockDifference,
            })
          } else {
            setSgHealth({
              status: SubgraphStatus.OK,
              currentBlock,
              chainHeadBlock: currentBlock,
              latestBlock,
              blockDifference,
            })
          }
        } catch (error) {
          console.error(`Failed to perform health check for ${subgraphUrl} subgraph`, error)
          setSgHealth({
            status: SubgraphStatus.DOWN,
            currentBlock: -1,
            chainHeadBlock: 0,
            latestBlock: -1,
            blockDifference: 0,
          })
        }
      }
      // `currentBlock` is 0 until the active chain's block number resolves. Without a
      // chain head to compare against there is nothing meaningful to report, and the
      // previous implementation silently fell back to the BSC head here.
      if (subgraphUrl && currentBlock) {
        getSubgraphHealth()
      }
    },
    [subgraphUrl],
  )

  return sgHealth
}

export default useSubgraphHealth
